import type { HttpErrorResponse } from '@angular/common/http';
import { Status } from '@essent/common';
import { emobilityActions } from '@innogy/emobility-shared';
import { validateSequentialIf } from '@innogy/utils-deprecated';
import {
  ProductSelection,
  type Consents,
} from '@integration/api-client/emobility';
import type { Action } from '@ngrx/store';
import { on } from '@ngrx/store';
import type { LinkField } from '@sitecore-jss/sitecore-jss-angular';
import { updateGroup } from 'ngrx-forms';
import { requiredTrue } from 'ngrx-forms/validation';
import {
  createProgressiveFormGroupState,
  createProgressiveNgrxFormReducerWithFormStateUpdate,
  type ProgressiveFormStateImplementor,
} from '@innogy/progressive-ngrx-forms';

import {
  initializeLeadForm,
  setE2eTrackingId,
  setEmobilityLeadFormType,
} from './lead-form.actions';

export const leadConsentsFormId = 'lead-consents';
export const callbackMomentControlId = `${leadConsentsFormId}.callbackMoment`;

export enum CallbackMoment {
  NoPreference = 'Geen voorkeur',
  Morning = 'Ochtend',
  Afternoon = 'Middag',
}

type ConsentsFormState = Required<Consents> & {
  callbackMoment: CallbackMoment | undefined;
};

export interface LeadConsentsFormState
  extends ProgressiveFormStateImplementor<ConsentsFormState> {
  thankYouPage?: LinkField;
  retryPage?: LinkField;
  postLeadFormStatus: Status;
  postLeadFormError?: HttpErrorResponse;
  leadFormType?: ProductSelection;
  duplicateCaseError: boolean;
  e2eTrackingId: string | undefined;
  loginPromptEnabled: boolean;
  paymentDetailsDisabled: boolean;
}

export const initialLeadConsentsFormState =
  createProgressiveFormGroupState<ConsentsFormState>(leadConsentsFormId, {
    acceptedCallFromAdvisor: false,
    acceptedPaymentConditions: false,
    acceptedTermsAndConditions: false,
    acceptedTariffRateChange: false,
    acceptedProductMail: false,
    callbackMoment: CallbackMoment.NoPreference,
  });

export const initialLeadConsentsState: LeadConsentsFormState = {
  progressiveForm: initialLeadConsentsFormState,
  thankYouPage: undefined,
  retryPage: undefined,
  postLeadFormStatus: Status.IDLE,
  postLeadFormError: undefined,
  leadFormType: undefined,
  duplicateCaseError: false,
  e2eTrackingId: undefined,
  loginPromptEnabled: false,
  paymentDetailsDisabled: false,
};

export const validateLeadConsentsForm = (state: LeadConsentsFormState) => {
  return updateGroup<ConsentsFormState>({
    acceptedCallFromAdvisor: validateSequentialIf(
      state.leadFormType === ProductSelection.CHARGING_STATION
    )(requiredTrue),
    acceptedPaymentConditions: validateSequentialIf(
      state.leadFormType === ProductSelection.CHARGE_CARD
    )(requiredTrue),
    acceptedTermsAndConditions: validateSequentialIf(
      state.leadFormType === ProductSelection.CHARGE_CARD
    )(requiredTrue),
  })(state.progressiveForm.formState);
};

const _reducer = createProgressiveNgrxFormReducerWithFormStateUpdate(
  initialLeadConsentsState,
  validateLeadConsentsForm,
  on(initializeLeadForm, (state, action) => {
    return {
      ...state,
      thankYouPage: action.payload.thankYouPageUrl,
      retryPage: action.payload.retryPageUrl,
      e2eTrackingId: action.payload.e2eTrackingId,
      loginPromptEnabled: action.payload.loginPromptEnabled,
      paymentDetailsDisabled: action.payload.paymentDetailsDisabled,
    };
  }),
  on(setE2eTrackingId, (state, action) => {
    return {
      ...state,
      e2eTrackingId: action.e2eTrackingId,
    };
  }),
  on(emobilityActions.postLeadV1.requestAction, (state) => {
    return {
      ...state,
      postLeadFormStatus: Status.PENDING,
      postLeadFormError: undefined,
      duplicateCaseError: false,
    };
  }),
  on(emobilityActions.postLeadV1.successAction, (state) => {
    return {
      ...state,
      postLeadFormStatus: Status.SUCCESS,
      postLeadFormError: undefined,
      duplicateCaseError: false,
    };
  }),
  on(emobilityActions.postLeadV1.errorAction, (state, action) => {
    return {
      ...state,
      postLeadFormStatus: Status.ERROR,
      postLeadFormError: action.payload,
      duplicateCaseError:
        action.payload.status === 400 &&
        action.payload.error?.code === 'existing_case',
    };
  }),
  on(setEmobilityLeadFormType, (state, action) => {
    return {
      ...state,
      leadFormType: action.proposition,
    };
  })
);

export function leadConsentsReducer(
  state: LeadConsentsFormState = initialLeadConsentsState,
  action: Action
): LeadConsentsFormState {
  return _reducer(state, action);
}
